/* eslint-disable qwik/no-use-visible-task */
import type { QRL, Signal } from "@builder.io/qwik";
import { $, component$, useOnDocument, useSignal, useStyles$, useVisibleTask$ } from "@builder.io/qwik";
import { FancyImage } from "~/common-components/fancy-image/FancyImage";
import { BREAKPOINTS } from "~/constants/mediaquery";
import { useNavigation } from "~/hooks/useNavigation";
import type { IGenAsset, IGenHeroSliderFullHeight, IGenVideo } from "~/services/graphql/__generated/sdk";
import { getInspectProps } from "~/utils/getInspectProps";
import { cn } from "~/utils/tailwind-helper";
import { INITIAL_CLIP_PATH_COORDS } from "./HeroSliderFullHeight";
import { ensureTextVisibility } from "~/utils/color-helper";
import EmbededVideo from "../../common-components/embeded-video/EmbededVideo";

interface HeroSliderFullHeightInnerProps extends IGenHeroSliderFullHeight {
    mediaSlides: (IGenAsset | IGenVideo)[];
    activeSlideIndex: Signal<number>;
    componentIndex: number;
    scaledownHandler: QRL<() => void>;
    transitionState: Signal<"IDLE" | "TRANSITIONING" | "DONE">;
    getNextSlide: QRL<() => number>;
    getPrevSlide: QRL<() => number>;
    backgroundImageSlide: Signal<IGenAsset | IGenVideo>;
    handleSlideAnimation: QRL<(activeIndex: number, e?: MouseEvent, centeredTransition?: boolean) => Promise<void>>;
    clipPathCoords: {
        x: number;
        y: number;
        scale: number;
    };
}

export const HeroSliderFullHeightInner = component$<HeroSliderFullHeightInnerProps>(
    ({
        mediaSlides,
        activeSlideIndex,
        componentIndex,
        cardButton,
        cardHeadline,
        cardTitle,
        id,
        scaledownHandler,
        transitionState,
        getNextSlide,
        getPrevSlide,
        backgroundImageSlide,
        handleSlideAnimation,
        clipPathCoords,
        colorOverwrite,
    }) => {
        useStyles$(
            `
            @keyframes progress-circle {
                from {
                    stroke-dashoffset: 122.5;
                }
                to {
                    stroke-dashoffset: 0;
                }
            }
        
            .animate-progress-circle {
                animation: progress-circle linear 1;
            }
            .video-container {
                transition: opacity 0.6s ease-in-out;
            }
            `,
        );

        const fallBackColor = "#22333B";
        const cardBackgroundColor = ensureTextVisibility(colorOverwrite || fallBackColor);

        let backgroundWithOpacity: string;

        switch (true) {
            case cardBackgroundColor.length === 7 && colorOverwrite && colorOverwrite.length === 9:
                backgroundWithOpacity = `${cardBackgroundColor}${colorOverwrite.slice(-2)}`;
                break;
            case cardBackgroundColor.length === 9:
                backgroundWithOpacity = cardBackgroundColor;
                break;
            default:
                backgroundWithOpacity = `${cardBackgroundColor.slice(0, 7)}33`;
                break;
        }

        const { getLink } = useNavigation();
        const AUTOPLAY_SPEED = 8000;
        const isAutoPlaying = useSignal(false);

        // Auto play timer handler
        useVisibleTask$(({ track, cleanup }) => {
            track(activeSlideIndex);
            track(isAutoPlaying);

            if (!isAutoPlaying.value) return;
            if ( mediaSlides && mediaSlides.length <= 1) {
                return;
            }

            let lastTime = 0;
            let animationFrameId: number;

            const animate = (time: number) => {
                if (!lastTime) lastTime = time;
                const deltaTime = time - lastTime;

                if (
                    (clipPathCoords.x === INITIAL_CLIP_PATH_COORDS.x &&
                        clipPathCoords.y === INITIAL_CLIP_PATH_COORDS.y) ||
                    window.innerWidth < BREAKPOINTS.SILVER
                ) {
                    clipPathCoords.x = window.innerWidth / 2;
                    clipPathCoords.y = window.innerHeight / 2;
                }

                if (deltaTime >= AUTOPLAY_SPEED) {
                    lastTime = time;
                    getNextSlide()
                        .then((nextSlide) => {
                            handleSlideAnimation(nextSlide);
                            backgroundImageSlide.value = mediaSlides![nextSlide];
                        })
                        .catch((error) => {
                            console.error("Error changing slide:", error);
                        });
                }

                animationFrameId = requestAnimationFrame(animate);
            };

            animationFrameId = requestAnimationFrame(animate);

            cleanup(() => {
                cancelAnimationFrame(animationFrameId);
            });
        });

        // turn auto play on dom-load
        useOnDocument(
            "DOMContentLoaded",
            $(() => {
                if (mediaSlides && mediaSlides.length > 1) {
                    isAutoPlaying.value = true;
                }
            }),
        );

        const commonButtonStyles =
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white-opacity-10 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.05)] backdrop-blur-[7px] text-white" as const;

        return (
            <>
                {mediaSlides?.map((item, index) => {
                    if (!item) return null;
                    const video = item.__typename == "Video" ? (item as IGenVideo) : undefined;
                    const asset = item.__typename == "Asset" ? (item as IGenAsset) : undefined;
                    return (
                        <div
                            key={"slide" + (item.id ?? index)}
                            class={cn(
                                "pointer-events-none absolute inset-0",
                                index === activeSlideIndex.value ? "z-10" : "z-[9]",
                            )}
                        >
                            {video && video.videoUrl ? (
                                <div class="h-full w-full">
                                    <div
                                        class={cn(
                                            "video-container absolute inset-0 z-10",
                                            // index === activeSlideIndex.value ? "opacity-100" : "opacity-0",
                                        )}
                                    >
                                        <EmbededVideo
                                            videoUrl={video.videoUrl}
                                            id={video.id!}
                                            autoplay={true}
                                            componentIndex={componentIndex}
                                            hideControls={true}
                                            scaleToFit={true}
                                            inSlider={true}
                                            ignoreMetaAspectRatio={true}
                                            class="h-full w-full"
                                        />
                                    </div>
                                </div>
                            ) : asset ? (
                                <FancyImage
                                    img={asset}
                                    lazyload={componentIndex > 0}
                                    responsiveAspectRatio={{
                                        bronze: 375 / 910,
                                        silver: 843 / 910,
                                        gold: 1280 / 910,
                                        platinum: 1440 / 1024,
                                    }}
                                    responsiveFactor={{
                                        bronze: 1,
                                        silver: 1,
                                        gold: 1,
                                        platinum: 1,
                                    }}
                                    class="h-full w-full"
                                />
                            ) : null}
                        </div>
                    );
                })}
                <div
                    class={cn(
                        "pointer-events-none absolute inset-0 z-30 mt-auto h-fit w-full",
                        "flex items-end justify-between gap-5 p-4",
                        "silver:p-8",
                        "gold:p-10",
                        "platinum:mx-auto platinum:max-w-[1440px]",
                    )}
                >
                    {/* Card */}
                    {(cardTitle || cardHeadline || cardButton) ? (
                        <div
                            class={cn(
                                "card-container",
                                // "border border-primary-500/20", no border wanted by LEICHT 
                                "pointer-events-auto flex min-w-[243px] flex-col rounded-lg p-5 backdrop-blur-md silver:min-w-[440px] silver:max-w-[552px] silver:p-8",
                            )}
                            style={{ backgroundColor: backgroundWithOpacity }}
                            onMouseEnter$={scaledownHandler}
                        >
                            {cardTitle && (
                                <h2
                                    {...getInspectProps({ id: id, fieldName: "cardTitle" })}
                                    class={cn("mb-3 text-base font-normal text-white-opacity-80 hyphens-none")}
                                    dangerouslySetInnerHTML={cardTitle.split("\n").join("<br />")}
                                />
                            )}
                            {cardHeadline && (
                                <h3
                                    {...getInspectProps({ id: id, fieldName: "cardHeadline" })}
                                    class={cn("mb-[42px] text-xl font-medium text-white silver:text-3xl hyphens-none")}
                                    dangerouslySetInnerHTML={cardHeadline.split("\n").join("<br />")}
                                />
                            )}
                            {cardButton?.label && getLink(cardButton)?.href && (
                                <a
                                    {...getInspectProps({ id: id, fieldName: "cardButton" })}
                                    {...getLink(cardButton)}
                                    class={cn(
                                        "flex w-fit items-center gap-2 border-b border-white-opacity-40 pb-1 text-base font-normal text-white transition-colors duration-300 hover:text-white/80",
                                    )}
                                >
                                    {cardButton.label}
                                    <span>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M8.53049 2.80268C8.2376 2.50978 7.76273 2.50978 7.46983 2.80268C7.17694 3.09557 7.17694 3.57044 7.46983 3.86334L10.8562 7.24967H3.3335C2.91928 7.24967 2.5835 7.58546 2.5835 7.99967C2.5835 8.41389 2.91928 8.74967 3.3335 8.74967H10.8562L7.46983 12.136C7.17694 12.4289 7.17694 12.9038 7.46983 13.1967C7.76273 13.4896 8.2376 13.4896 8.53049 13.1967L13.1972 8.53C13.3436 8.38356 13.4168 8.19162 13.4168 7.99967C13.4168 7.89798 13.3966 7.80102 13.3599 7.71259C13.3233 7.62413 13.2691 7.54125 13.1972 7.46934L8.53049 2.80268Z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                    </span>
                                </a>
                            )}
                        </div>
                    ): <span></span>}

                    {/* Navigation */}
                    <div
                        class={cn("navigation-btns-container", "pointer-events-auto flex w-fit flex-col gap-2")}
                        onMouseEnter$={scaledownHandler}
                    >
                        {mediaSlides && mediaSlides.length > 1 && (
                            <>
                                {/* play/pause */}
                                <button
                                    class={cn(
                                        commonButtonStyles,
                                        "relative border border-transparent transition-colors duration-200",
                                        !isAutoPlaying.value && "border-white-opacity-20",
                                    )}
                                    onClick$={() => (isAutoPlaying.value = !isAutoPlaying.value)}
                                >
                                    {/* Progress Spinner */}
                                    {isAutoPlaying.value && (
                                        <span class={cn("absolute inset-0 flex items-center justify-center")}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 41 41"
                                                class="h-10 w-10"
                                            >
                                                {/* Background circle */}
                                                <circle
                                                    cx="20.5"
                                                    cy="20.5"
                                                    r="19.5"
                                                    fill="none"
                                                    stroke="rgba(255,255,255,0.2)"
                                                    stroke-width="1.5"
                                                />

                                                {/* Animated progress arc */}
                                                <path
                                                    key={`progress-${activeSlideIndex.value}`}
                                                    d="M 20.5 1 A 19.5 19.5 0 0 1 20.5 40 A 19.5 19.5 0 0 1 20.5 1"
                                                    fill="none"
                                                    stroke="white"
                                                    stroke-width="1"
                                                    stroke-linecap="round"
                                                    stroke-dasharray="122.5"
                                                    stroke-dashoffset="122.5"
                                                    class={cn(
                                                        transitionState.value !== "TRANSITIONING" &&
                                                            "animate-progress-circle",
                                                    )}
                                                    style={{
                                                        animationDuration: `${AUTOPLAY_SPEED}ms`,
                                                    }}
                                                ></path>
                                            </svg>
                                        </span>
                                    )}
                                    {isAutoPlaying.value ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="10"
                                            height="12"
                                            viewBox="0 0 10 12"
                                            fill="none"
                                        >
                                            <rect width="2" height="12" rx="1" fill="currentColor" />
                                            <rect x="8" width="2" height="12" rx="1" fill="currentColor" />
                                        </svg>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                        >
                                            <path
                                                d="M6 4v12l10-6-10-6z"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    )}
                                </button>
                                {/* Prev */}
                                <button
                                    class={cn(commonButtonStyles)}
                                    disabled={transitionState.value === "TRANSITIONING"}
                                    onClick$={async () => {
                                        const prevSlide = await getPrevSlide();
                                        handleSlideAnimation(prevSlide, undefined, true);
                                        backgroundImageSlide.value = mediaSlides![prevSlide];
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                    >
                                        <path
                                            d="M12.5 15L7.5 10L12.5 5"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>
                                {/* Next */}
                                <button
                                    class={cn(commonButtonStyles)}
                                    disabled={transitionState.value === "TRANSITIONING"}
                                    onClick$={async () => {
                                        const nextSlide = await getNextSlide();
                                        await handleSlideAnimation(nextSlide, undefined, true);
                                        backgroundImageSlide.value = mediaSlides![nextSlide];
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                    >
                                        <path
                                            d="M7.5 15L12.5 10L7.5 5"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>
                            </>
                        )}
                        {/* scroll down */}
                        <button
                            class={cn(commonButtonStyles, "h-[60px]")}
                            onClick$={() => {
                                window.scrollTo({ top: 50, behavior: "smooth" });
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="8"
                                height="37"
                                viewBox="0 0 8 37"
                                fill="none"
                            >
                                <path
                                    d="M3.64645 36.3536C3.84171 36.5488 4.15829 36.5488 4.35355 36.3536L7.53553 33.1716C7.7308 32.9763 7.7308 32.6597 7.53553 32.4645C7.34027 32.2692 7.02369 32.2692 6.82843 32.4645L4 35.2929L1.17157 32.4645C0.976311 32.2692 0.659728 32.2692 0.464466 32.4645C0.269204 32.6597 0.269204 32.9763 0.464466 33.1716L3.64645 36.3536ZM3.5 0V36H4.5V0H3.5Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </>
        );
    },
);
